/**
 * VideoTrimService
 * Frame-accurate video trimming using FFmpeg.wasm
 * 
 * Features:
 * - Remove frames from start (fixes first-frame artifacts)
 * - Remove frames from end (fixes Veo3 last-frame issues)
 * - Lazy-loads FFmpeg only when needed (25MB one-time download)
 * - Frame-accurate trimming with proper MP4 output
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

export interface TrimOptions {
  /** Number of frames to remove from the start */
  trimStart?: number;
  /** Number of frames to remove from the end */
  trimEnd?: number;
  /** Time in seconds to trim from start (alternative to frames) */
  trimStartSeconds?: number;
  /** Time in seconds to trim from end (alternative to frames) */
  trimEndSeconds?: number;
}

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
  frameRate: number;
  estimatedFrameCount: number;
}

type LoadingCallback = (message: string, progress?: number) => void;

class VideoTrimServiceClass {
  private static instance: VideoTrimServiceClass;
  private ffmpeg: FFmpeg | null = null;
  private ffmpegLoading: Promise<void> | null = null;
  private ffmpegLoaded = false;

  private constructor() {}

  static getInstance(): VideoTrimServiceClass {
    if (!VideoTrimServiceClass.instance) {
      VideoTrimServiceClass.instance = new VideoTrimServiceClass();
    }
    return VideoTrimServiceClass.instance;
  }

  /**
   * Check if video trimming is supported
   */
  isSupported(): boolean {
    // FFmpeg.wasm requires SharedArrayBuffer which needs specific headers
    // But we can still try - it will fail gracefully if not supported
    return typeof SharedArrayBuffer !== 'undefined' || true; // Allow attempt
  }

  /**
   * Check if FFmpeg is already loaded
   */
  isLoaded(): boolean {
    return this.ffmpegLoaded;
  }

  /**
   * Load FFmpeg.wasm (lazy-loaded on first use)
   */
  async loadFFmpeg(onProgress?: LoadingCallback): Promise<void> {
    if (this.ffmpegLoaded) return;
    
    // If already loading, wait for it
    if (this.ffmpegLoading) {
      await this.ffmpegLoading;
      return;
    }

    this.ffmpegLoading = this._loadFFmpegInternal(onProgress);
    await this.ffmpegLoading;
  }

  private async _loadFFmpegInternal(onProgress?: LoadingCallback): Promise<void> {
    try {
      onProgress?.('Initializing video tools...', 0);
      
      // Check if SharedArrayBuffer is available (required for FFmpeg.wasm)
      if (typeof SharedArrayBuffer === 'undefined') {
        console.warn('SharedArrayBuffer not available - FFmpeg requires COOP/COEP headers');
      }
      
      this.ffmpeg = new FFmpeg();
      
      // Set up progress logging
      this.ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg]', message);
      });

      this.ffmpeg.on('progress', ({ progress }) => {
        onProgress?.('Processing video...', progress);
      });

      onProgress?.('Downloading video tools (one-time setup)...', 0.1);

      // Load FFmpeg core from CDN
      // Using unpkg.com which has proper CORS headers
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
      
      try {
        const coreUrl = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
        const wasmUrl = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
        
        await this.ffmpeg.load({
          coreURL: coreUrl,
          wasmURL: wasmUrl,
        });
      } catch (loadError) {
        console.error('Error loading FFmpeg from CDN:', loadError);
        throw new Error(`Failed to download video tools: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
      }

      this.ffmpegLoaded = true;
      onProgress?.('Video tools ready!', 1);
      
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
      this.ffmpegLoading = null;
      const errorMessage = error instanceof Error ? error.message : 'Failed to load video tools. Please try again.';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get video metadata from a URL or Blob
   */
  async getVideoMetadata(source: string | Blob): Promise<VideoMetadata> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.muted = true;
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        // Estimate frame rate - most AI videos are 24fps
        const frameRate = 24;
        const estimatedFrameCount = Math.round(video.duration * frameRate);

        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          frameRate,
          estimatedFrameCount,
        });

        if (typeof source !== 'string') {
          URL.revokeObjectURL(video.src);
        }
      };

      video.onerror = () => {
        reject(new Error('Failed to load video metadata'));
        if (typeof source !== 'string') {
          URL.revokeObjectURL(video.src);
        }
      };

      if (typeof source === 'string') {
        video.src = source;
      } else {
        video.src = URL.createObjectURL(source);
      }
    });
  }

  /**
   * Trim video using FFmpeg.wasm (frame-accurate)
   */
  async trimVideo(
    source: string | Blob,
    options: TrimOptions,
    onProgress?: (progress: number, message?: string) => void
  ): Promise<Blob> {
    // Load FFmpeg if not already loaded
    if (!this.ffmpegLoaded) {
      await this.loadFFmpeg((message, progress) => {
        onProgress?.(progress ?? 0, message);
      });
    }

    if (!this.ffmpeg) {
      throw new Error('FFmpeg not initialized');
    }

    const metadata = await this.getVideoMetadata(source);
    const { trimStart = 0, trimEnd = 0, trimStartSeconds, trimEndSeconds } = options;

    // Calculate time-based trim points
    const startTime = trimStartSeconds ?? (trimStart / metadata.frameRate);
    const endTrimDuration = trimEndSeconds ?? (trimEnd / metadata.frameRate);
    const newDuration = metadata.duration - startTime - endTrimDuration;

    if (newDuration <= 0) {
      throw new Error('Cannot trim more than the video contains');
    }

    onProgress?.(0.1, 'Preparing video...');

    // Fetch the video file
    let videoData: Uint8Array;
    if (typeof source === 'string') {
      videoData = await fetchFile(source);
    } else {
      videoData = await fetchFile(source);
    }

    // Write input file to FFmpeg virtual filesystem
    await this.ffmpeg.writeFile('input.mp4', videoData);

    onProgress?.(0.2, 'Trimming video (re-encoding for accuracy)...');

    // Build FFmpeg command for frame-accurate trimming
    // We MUST re-encode for frame-accurate cuts (stream copy only cuts at keyframes)
    // Using -ss after -i for frame-accurate seeking (slower but precise)
    const args = [
      '-i', 'input.mp4',
    ];
    
    if (startTime > 0) {
      args.push('-ss', startTime.toFixed(3));
    }
    
    args.push('-t', newDuration.toFixed(3));
    
    // Re-encode using x264 for frame-accurate trimming
    // Note: FFmpeg.wasm includes libx264
    args.push(
      '-c:v', 'libx264',
      '-preset', 'ultrafast',  // Fast encoding for browser
      '-crf', '18',            // High quality (lower = better, 18 is visually lossless)
      '-pix_fmt', 'yuv420p',   // Ensure compatibility
      '-c:a', 'aac',
      '-b:a', '192k',
      '-movflags', '+faststart',
      '-y',                    // Overwrite output
      'output.mp4'
    );

    // Set up progress tracking
    this.ffmpeg.on('progress', ({ progress }) => {
      // Map progress from 0.2 to 0.9
      onProgress?.(0.2 + progress * 0.7, 'Trimming video...');
    });

    // Run FFmpeg
    await this.ffmpeg.exec(args);

    onProgress?.(0.9, 'Finalizing...');

    // Read the output file
    const outputData = await this.ffmpeg.readFile('output.mp4');
    
    // Clean up
    await this.ffmpeg.deleteFile('input.mp4');
    await this.ffmpeg.deleteFile('output.mp4');

    onProgress?.(1, 'Done!');

    // Create blob from output - convert FileData to ArrayBuffer first
    let arrayBuffer: ArrayBuffer;
    if (outputData instanceof Uint8Array) {
      // Create a copy to ensure we get a pure ArrayBuffer (not SharedArrayBuffer)
      const copy = new Uint8Array(outputData);
      arrayBuffer = copy.buffer;
    } else if (typeof outputData === 'string') {
      // Handle string case by encoding to Uint8Array
      const encoder = new TextEncoder();
      const uint8 = encoder.encode(outputData);
      arrayBuffer = uint8.buffer;
    } else {
      arrayBuffer = outputData as ArrayBuffer;
    }
    return new Blob([arrayBuffer], { type: 'video/mp4' });
  }

  /**
   * Create a trimmed video and return as a Blob URL
   */
  async createTrimmedVideo(
    source: string | Blob,
    options: TrimOptions,
    onProgress?: (progress: number, message?: string) => void
  ): Promise<string> {
    const trimmedBlob = await this.trimVideo(source, options, onProgress);
    return URL.createObjectURL(trimmedBlob);
  }
}

export const videoTrimService = VideoTrimServiceClass.getInstance();
export default videoTrimService;
